import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { propertiesService } from "../services/firebaseServices";
import { contentService } from "../services/contentService"; // Import content service
import HospitableWidget from "../components/HospitableWidget";
import SEO from "../components/SEO";

const Home = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for dynamic text content
  const [content, setContent] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [propsData, contentData] = await Promise.all([
          propertiesService.getAll(),
          contentService.getPageContent("home"),
        ]);

        setProperties(propsData);
        if (contentData) setContent(contentData);
      } catch (err) {
        console.error("Failed to load data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Use content from DB if available, otherwise use defaults
  const text = {
    h1:
      content?.h1 ||
      "Boutique Luxury Vacation Homes in Mountain & Coastal Destinations",
    introText:
      content?.introText ||
      "ACF Luxury Stays offers a deliberately hand-selected collection of premier residences – from slopeside chalets in Telluride and Beaver Creek to a luxury poolside retreat in Del Mar, San Diego.",
    secondary_intro_p1:
      content?.secondary_intro_p1 ||
      "Discover why we’ve chosen to call these homes our own as you enjoy the rugged peaks of the San Juan Mountains or sun-drenched tranquility of the Southern California coast.",
    secondary_intro_p2:
      content?.secondary_intro_p2 ||
      "As family owners who personally oversee every detail, we deliver a level of intentionality, care, and hotel-standard accountability that only true ownership can provide. Experience the warmth of a private home, meticulously curated for the most discerning travelers planning their next vacation.",
    h2_destinations:
      content?.h2_destinations || "Explore Our Luxury Vacation Homes",
    h2_why_choose: content?.h2_why_choose || "Why Choose ACF Luxury Stays?",
    why_choose_subtext:
      content?.why_choose_subtext ||
      "We offer a deliberately small collection of luxury homes, each personally overseen and professionally managed to hotel-level standards.",
    testimonial_1:
      content?.testimonial_1 ||
      "Chris was the most incredible host - a top notch experience from the very beginning. Chris and team were extremely responsive, friendly, and helpful. The property is pristine and in a great relocation with Westin amenities. Can’t recommend this property enough!! Thank you for an amazing stay.",
    testimonial_2:
      content?.testimonial_2 ||
      "This place was above our expectations. The cozy feel of his place was exactly what we were looking for. Everything in mountain village is in walking distance as advertised. Our family is already making plans for a return stay. Chris would respond immediately to any questions we had. I would highly recommend this rental for a stay anytime of the year.",
  };

  return (
    <div className="pt-24">
      <SEO
        title="Boutique Luxury Vacation Rentals in CO, CA & FL | ACF Luxury Stays"
        description="Book direct for boutique luxury vacation rentals in Telluride, Beaver Creek, San Diego, and Miami Beach. Save on booking fees at our ski-in/ski-out chalets, private pool coastal retreats, and oceanfront residences."
        url="/"
      />

      {/* Hero Section */}
      <div className="relative h-[80vh] w-full overflow-hidden bg-black">
        <video
          className="absolute top-0 left-0 w-full h-full object-cover opacity-60"
          autoPlay
          loop
          muted
          playsInline
          poster="/images/hero-bg.jpg"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center max-w-4xl px-4">
            <h1 className="text-white text-3xl md:text-5xl font-primary mb-6 drop-shadow-lg leading-tight">
              {text.h1}
            </h1>
            <p className="text-white text-lg md:text-xl font-secondary mb-8 leading-relaxed drop-shadow-md hidden md:block">
              {text.introText}
            </p>
            <Link
              to="/homes"
              className="bg-acf-gold text-white px-8 py-3 font-primary uppercase tracking-widest hover:bg-white hover:text-acf-gold transition duration-300 no-underline"
            >
              Explore the Collection
            </Link>
          </div>
        </div>
      </div>

      {/* Secondary Intro */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-24 h-1 bg-acf-gold mx-auto mb-8"></div>
          <p className="font-secondary text-lg text-gray-700 font-bold leading-relaxed mb-6">
            {text.secondary_intro_p1}
          </p>
          <p className="font-secondary text-lg text-gray-700 font-bold leading-relaxed">
            {text.secondary_intro_p2}
          </p>
        </div>
      </div>

      {/* Hospitable Search Widget Section */}
      <div className="bg-acf-blue py-12" id="book-now">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-acf-gold font-primary mb-8 text-5xl md:text-6xl font-bold tracking-widest">
            Reserve Your Stay
          </h2>
          <HospitableWidget compact={false} />
        </div>
      </div>

      {/* Properties Preview */}
      <div className="py-20 max-w-7xl mx-auto px-4">
        <h2 className="text-center text-3xl md:text-4xl font-primary text-acf-blue mb-12">
          {text.h2_destinations}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {loading ? (
            <div className="col-span-1 md:col-span-2 text-center py-10 text-gray-500">
              Loading properties...
            </div>
          ) : properties.length > 0 ? (
            properties.map((property) => {
              let mainPhotoUrl = null;
              if (property.photos && property.photos.length > 0) {
                mainPhotoUrl = property.photos[0].url;
              } else if (property.image) {
                mainPhotoUrl = property.image;
              }

              return (
                <DestinationCard
                  key={property.id}
                  title={property.title}
                  location={property.location}
                  desc={
                    property.headline
                      ? property.headline
                      : property.description
                        ? property.description.substring(0, 100) + "..."
                        : "Experience luxury living in this stunning vacation home."
                  }
                  img={mainPhotoUrl}
                  link={`/homes/${property.slug || property.id}`}
                />
              );
            })
          ) : (
            <div className="col-span-1 md:col-span-2 text-center text-gray-500">
              No properties found.
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/homes"
            className="bg-acf-blue text-white px-8 py-3 font-primary uppercase tracking-widest hover:bg-acf-gold transition duration-300 no-underline"
          >
            Explore the Collection
          </Link>
        </div>
      </div>

      {/* Why Choose Us & Testimonials */}
      <div className="bg-acf-slate py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-primary text-acf-blue mb-6">
              {text.h2_why_choose}
            </h2>
            <div className="w-24 h-1 bg-acf-gold mx-auto mb-8"></div>
            <p className="font-secondary text-lg text-gray-700 leading-relaxed">
              {text.why_choose_subtext}
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white p-8 rounded-sm shadow-sm border-t-4 border-acf-gold">
              <div className="text-acf-gold text-4xl font-serif mb-4">“</div>
              <p className="font-secondary text-gray-600 italic mb-6">
                {text.testimonial_1}
              </p>
              <div className="font-primary text-acf-blue text-sm uppercase tracking-wider">
                — Ariana, Beaver Creek Guest
              </div>
            </div>

            <div className="bg-white p-8 rounded-sm shadow-sm border-t-4 border-acf-gold">
              <div className="text-acf-gold text-4xl font-serif mb-4">“</div>
              <p className="font-secondary text-gray-600 italic mb-6">
                {text.testimonial_2}
              </p>
              <div className="font-primary text-acf-blue text-sm uppercase tracking-wider">
                — Jason, Telluride Guest
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Book Direct - Full Width Section */}
      <div className="bg-acf-blue py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-primary mb-12 text-white">
            Why Book Direct?
          </h2>
          <div className="grid md:grid-cols-3 gap-12 font-secondary text-white text-sm md:text-base mb-12">
            {/* Best Rates - Tag Icon */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-acf-gold flex items-center justify-center mb-6 shadow-xl transform transition-transform hover:scale-105">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>
              <h4 className="font-primary text-xl mb-3 text-acf-gold">
                Best Rate Guarantee
              </h4>
              <p className="opacity-90 max-w-xs leading-relaxed">
                Always receive the lowest available price when you book directly
                with us.
              </p>
            </div>

            {/* No Fees - Shield Check Icon */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-acf-gold flex items-center justify-center mb-6 shadow-xl transform transition-transform hover:scale-105">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h4 className="font-primary text-xl mb-3 text-acf-gold">
                Zero Service Fees
              </h4>
              <p className="opacity-90 max-w-xs leading-relaxed">
                Save up to 15% by avoiding third-party platform commissions and
                hidden charges.
              </p>
            </div>

            {/* Direct Support - Bell Icon */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-acf-gold flex items-center justify-center mb-6 shadow-xl transform transition-transform hover:scale-105">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <h4 className="font-primary text-xl mb-3 text-acf-gold">
                Concierge Support
              </h4>
              <p className="opacity-90 max-w-xs leading-relaxed">
                Direct access to our family team for personalized
                recommendations and assistance.
              </p>
            </div>
          </div>

          <div>
            <a
              href="#book-now"
              className="inline-block bg-acf-gold text-white px-10 py-4 font-primary uppercase tracking-widest hover:bg-white hover:text-acf-gold transition duration-300 no-underline text-lg shadow-md"
            >
              Book Your Stay
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// DestinationCard Component
const DestinationCard = ({ title, location, desc, img, link }) => (
  <Link to={link} className="group cursor-pointer block no-underline">
    <div className="h-64 overflow-hidden mb-4 bg-gray-200 relative flex items-center justify-center">
      {img ? (
        <img
          src={img}
          alt={title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentElement.innerHTML =
              '<span class="text-gray-400">Image Unavailable</span>';
          }}
        />
      ) : (
        <div className="text-center p-4">
          <span className="text-acf-blue font-bold block mb-1 opacity-50">
            NO IMAGE
          </span>
        </div>
      )}
    </div>
    <h3 className="text-xl font-primary text-acf-blue mb-1">{title}</h3>

    {location && (
      <p className="text-acf-gold font-primary text-xs uppercase tracking-widest mb-3">
        {location}
      </p>
    )}

    <p className="font-secondary text-gray-600 text-sm line-clamp-3">{desc}</p>
  </Link>
);

export default Home;
